import styled, { css } from 'styled-components';
import breakpoint from 'Styles/breakpoints';
import PaginationDots from '../PaginationDots/PaginationDots';

const smallSidePadding = '1.5rem';
const mediumSidePadding = '3rem';
const largeSidePadding = '0';

export const CarouselContainer = styled.section`
  position: relative;
  margin: 0 auto;

  * {
    box-sizing: border-box;
  }
`;

export const Carousel = styled.ul`
  position: relative;
  overflow-x: auto;
  overflow-y: hidden;
  display: flex;
  flex-direction: row;
  scroll-padding-left: ${smallSidePadding};

  ${({ isDragging, hasSnappingRule }) =>
    isDragging
      ? ''
      : `
      ${hasSnappingRule ? 'scroll-snap-type: x mandatory;' : ''}
    scroll-snap-stop: always;
  `}
  padding: 0;
  -webkit-overflow-scrolling: touch;

  /* Hide scrollbar; all browsers */
  -ms-overflow-style: none; /* Edge */
  scrollbar-width: none; /* Firefox */
  ::-webkit-scrollbar {
    display: none;
  }

  ${({ $styleQueryMode }) => css`
    ${breakpoint('medium', { mode: $styleQueryMode })} {
      scroll-padding-left: ${mediumSidePadding};
    }

    ${breakpoint('large', { mode: $styleQueryMode })} {
      scroll-padding-left: ${largeSidePadding};
    }
  `}
`;

export const Content = styled.li`
  box-sizing: content-box;
  list-style-type: none;
  scroll-snap-align: ${({ numSlides, withPeeking }) =>
    numSlides?.small === 1 && !withPeeking ? 'end' : 'start'};
  transition: transform;
  margin-right: ${({ withMargin }) => withMargin && '1rem'};

  ${(props) => {
    const slidesOnSmallScreen = props.numSlides.small;
    const width = (100 / slidesOnSmallScreen).toFixed(2);
    /**
     * The numerators are roughly the percent widths of 1 or 2 columns in a 12 column layout
     */
    const oneColumnOffset = (
      (slidesOnSmallScreen === 1 ? 16 : 8) / slidesOnSmallScreen
    ).toFixed(2);

    /**
     * If there are 3 slides on small screen, set the card width to 38%
     */
    if (slidesOnSmallScreen === 3) {
      return `
        max-width: 38%;
        flex: 0 0 38%;
      `;
    }

    if (!props.withPeeking) {
      return `
        max-width: ${width}%;
        flex: 0 0 ${width}%;
      `;
    }

    /**
     * To control the amount of slides we want to show at a time
     * we divide 100% by the amount of cards we want to show.
     *
     * For example, 100% / 3 cards = 33.33%
     *
     * To make a slide peek, we subtract x columns from flex-basis (x columns is a spec from design)
     * For example, if we want to show 2 cards with peeking
     * (100% / 2) - (16.67% / 2) = ~42%
     *
     * When the flex basis is 42%, two whole cards will show at 84%
     * The remainder showing will be 16% of the next card.
     */
    return `
      max-width: calc(${width}% - ${oneColumnOffset}% - 1rem);
      flex: 0 0 calc(${width}% - ${oneColumnOffset}% - 1rem);
    `;
  }}

  ${({ withMargin, numSlides, withPeeking, $styleQueryMode }) => {
    const slidesOnMediumScreen = numSlides.medium || numSlides.small;
    const width = (100 / slidesOnMediumScreen).toFixed(2);
    const oneColumnOffset = (8 / slidesOnMediumScreen).toFixed(2);

    const peekingStyles = withPeeking
      ? `max-width: calc(${width}% - ${oneColumnOffset}% - 1.5rem);
      flex: 0 0 calc(${width}% - ${oneColumnOffset}% - 1.5rem);`
      : `max-width: ${width}%; flex: 0 0 ${width}%;`;
    
    return css`
      ${breakpoint('medium', { mode: $styleQueryMode})} {
        margin-right: ${withMargin && '1.5rem'};
        scroll-snap-align: ${numSlides?.medium === 1 && !withPeeking ? 'end' : 'start'};
        ${peekingStyles}
      }
    `;
  }}

  ${({ numSlides, withPeeking, $styleQueryMode }) => {
    const slidesOnLargeScreen = numSlides.large || numSlides.small;
    const width = (100 / slidesOnLargeScreen).toFixed(2);
    const oneColumnOffset = 8 / slidesOnLargeScreen;

    const peekingStyles = withPeeking
      ? `max-width: calc(${width}% - ${oneColumnOffset}% - 1.5rem);
    flex: 0 0 calc(${width}% - ${oneColumnOffset}% - 1.5rem);`
      : `max-width: ${width}%;
    flex: 0 0 calc(${width}% - ${10 + 2 * slidesOnLargeScreen}px);`;

    return css`
      ${breakpoint('large', { mode: $styleQueryMode })} {
        scroll-snap-align: ${numSlides?.large === 1 && !withPeeking ? 'end' : 'start'};
        ${peekingStyles}
      }
    `;
  }}

  &:first-child {
    padding-left: ${(props) => props.withPadding && smallSidePadding};

    ${({ $styleQueryMode }) => css`
      ${breakpoint('medium', { mode: $styleQueryMode })} {
        padding-left: ${(props) => props.withPadding && mediumSidePadding};
      }

      ${breakpoint('large', { mode: $styleQueryMode })} {
        padding-left: ${(props) => props.withPadding && largeSidePadding};
      }
    `}
  }

  &:last-child {
    padding-right: ${(props) => props.withPadding && smallSidePadding};
    scroll-snap-align: end;

    ${({ $styleQueryMode }) => css`
      ${breakpoint('medium', { mode: $styleQueryMode })} {
        padding-right: ${(props) => props.withPadding && mediumSidePadding};
      }

      ${breakpoint('large', { mode: $styleQueryMode })} {
        padding-right: ${(props) => props.withPadding && largeSidePadding};
      }
    `}
  }

  & > * {
    width: 100%;
    height: 100%;
  }
`;

export const CarouselDirection = styled.button`
  display: none;
  z-index: 2;

  ${({ showDots, direction, $withArrowButtonVerticalOffset, $styleQueryMode}) => css`
    ${breakpoint('large', { mode: $styleQueryMode })} {
      display: ${showDots ? 'none' : 'flex'};
      justify-content: center;
      align-items: center;
      position: absolute;
      background: #fff;
      border-radius: 50%;
      height: 30px;
      width: 30px;
      /**
      * Vertically center at the image
      */
      transform: translate(0, -50%);

      /* Account for title below image, or center perfectly */
      ${$withArrowButtonVerticalOffset ? 'top: calc(50% - 16.5px);' : 'top: 50%;'}

      box-shadow: ${direction === 'next' ? '2px' : '-2px'} 2px 4px 0
      rgba(0, 0, 0, 0.25);
      font-size: 1.2rem;
      font-weight: bold;
      cursor: pointer;
      padding: 0;
      border: 0;
      ${direction === 'next' ? 'right: -12px;': 'left: -12px;'}
    }
  `}
`;

export const CarouselDots = styled(PaginationDots)`
  position: absolute;
  bottom: 24px;
  width: 100%;
  justify-content: center;
`;
